/**
 * Package Context
 *
 * Provides package/plan data to the entire application
 */

import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { usePackage } from '@/hooks/usePackage';
import { EntitlementsPayload, TeamPackageResponse } from '@/types/package';
import { useGatingStore } from '@/stores/gating.store';

/**
 * Package Context Type
 */
interface PackageContextType {
  data: TeamPackageResponse | null | undefined;
  isLoading: boolean;
  error: Error | null;
  isExpired: boolean;
  /** Resolved entitlements + gating mode; null = resolver failed / not loaded (UI locks nothing) */
  entitlements: EntitlementsPayload | null;

  // Helper functions
  hasFeature: (featureName: string) => boolean;
  getLimit: (featureName: string) => number | null; // null = unlimited
  getUsage: (featureName: string) => number;
  isWithinLimit: (featureName: string) => boolean;
  canAdd: (featureName: string) => boolean;
  getRemainingQuota: (featureName: string) => number;
}

export const PackageContext = createContext<PackageContextType | undefined>(undefined);

interface PackageProviderProps {
  children: ReactNode;
}

/**
 * Package Provider
 *
 * Fetches and provides package data when user is authenticated
 */
export const PackageProvider = ({ children }: PackageProviderProps) => {
  const { isAuthenticated } = useAuth();

  // Only fetch package data if user is authenticated
  // Pass isAuthenticated to prevent race condition where query fires before auth is ready
  const packageQuery = usePackage(isAuthenticated);

  // Mirror the gating mode into the store so the axios interceptor (outside React) can read it
  const gatingMode = packageQuery.entitlements?.mode ?? 'off';
  useEffect(() => {
    useGatingStore.getState().setMode(gatingMode);
  }, [gatingMode]);

  // If not authenticated, return a mock context with loading state
  if (!isAuthenticated) {
    const mockContext: PackageContextType = {
      data: undefined,
      isLoading: false,
      error: null,
      isExpired: false,
      entitlements: null,
      hasFeature: () => false,
      getLimit: () => null,
      getUsage: () => 0,
      isWithinLimit: () => false,
      canAdd: () => false,
      getRemainingQuota: () => 0,
    };

    return (
      <PackageContext.Provider value={mockContext}>
        {children}
      </PackageContext.Provider>
    );
  }

  const value: PackageContextType = {
    data: packageQuery.data,
    isLoading: packageQuery.isLoading,
    error: packageQuery.error,
    isExpired: packageQuery.isExpired,
    entitlements: packageQuery.entitlements,
    hasFeature: packageQuery.hasFeature,
    getLimit: packageQuery.getLimit,
    getUsage: packageQuery.getUsage,
    isWithinLimit: packageQuery.isWithinLimit,
    canAdd: packageQuery.canAdd,
    getRemainingQuota: packageQuery.getRemainingQuota,
  };

  return (
    <PackageContext.Provider value={value}>
      {children}
    </PackageContext.Provider>
  );
};

/**
 * Hook to consume Package Context
 *
 * @throws {Error} if used outside PackageProvider
 */
export const usePackageContext = (): PackageContextType => {
  const context = useContext(PackageContext);
  if (!context) {
    throw new Error('usePackageContext must be used within PackageProvider');
  }
  return context;
};
